import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import {
  ArrowRight, Heart, Brain, Sparkles, MessageSquare, Briefcase,
  Target, BookOpen, Globe, Volume2, Shield, Fingerprint, Users,
  Zap, Download, Crown, Star, ChevronRight, Check, Quote,
  Mail, Loader2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

/* ─── Animation Helpers ─── */
function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: delay * 0.001, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function AnimCounter({ value, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);
  const numVal = parseInt(value);
  useEffect(() => {
    if (!inView || isNaN(numVal)) return;
    let start = 0;
    const step = Math.max(1, Math.floor(numVal / 40));
    const timer = setInterval(() => {
      start += step;
      if (start >= numVal) { setCount(numVal); clearInterval(timer); }
      else setCount(start);
    }, 30);
    return () => clearInterval(timer);
  }, [inView, numVal]);

  return (
    <span ref={ref}>{isNaN(numVal) ? value : count}{suffix}</span>
  );
}

/* ─── Constants ─── */
const MODULES = [
  { icon: Heart, name: "The Friend", desc: "Casual chat, emotional support, and a companion who remembers your story.", span: "md:col-span-4", color: "#E31212" },
  { icon: Sparkles, name: "The Mentor", desc: "Wisdom tailored to your beliefs — from scripture to philosophy to humanist thought.", span: "md:col-span-4", color: "#F5A623" },
  { icon: MessageSquare, name: "The Social Guide", desc: "Role-play difficult conversations and navigate social dynamics with confidence.", span: "md:col-span-4", color: "#34D399" },
  { icon: Briefcase, name: "The Job Guardian", desc: "Resume mastery, interview prep, salary negotiation, and workplace navigation.", span: "md:col-span-4", color: "#A78BFA" },
  { icon: Target, name: "The Life Coach", desc: "Transform goals into daily micro-actions. Stay accountable. Build momentum.", span: "md:col-span-4", color: "#22D3EE" },
  { icon: BookOpen, name: "The Storyteller", desc: "Personalized fables drawn from your cultural traditions. Wisdom through narrative.", span: "md:col-span-4", color: "#FB923C" },
];

const MENTORS = [
  "Rumi", "Cleopatra", "Marcus Aurelius", "Maya Angelou", "Confucius",
  "Frida Kahlo", "Leonardo da Vinci", "Nikola Tesla", "Hypatia",
  "Sun Tzu", "Marie Curie", "Shakespeare", "Buddha", "Socrates",
  "Ada Lovelace", "Ibn Battuta", "Harriet Tubman", "Lao Tzu",
  "Nelson Mandela", "Amelia Earhart", "Galileo", "Nefertiti",
];

const CAPABILITIES = [
  { icon: Volume2, title: "Voice Conversations", desc: "Speak naturally and hear responses read aloud in lifelike voices." },
  { icon: Heart, title: "Mood Tracking", desc: "Log emotions daily. Visualize patterns with heatmaps and weekly insights." },
  { icon: Brain, title: "AI Memory", desc: "Your companion remembers your preferences, history, and journey over time." },
  { icon: Target, title: "Goals & Streaks", desc: "Set personal goals with AI coaching. Build daily habits and stay accountable.", },
  { icon: Fingerprint, title: "Biometric Login", desc: "Fingerprint, Face ID, or Windows Hello. Secure access, zero friction." },
  { icon: Users, title: "Family Plans", desc: "One subscription, multiple members. Age-appropriate AI for every family member." },
  { icon: Zap, title: "Smart Greetings", desc: "Culturally-aware greetings in your native language, aware of time and occasion." },
  { icon: Download, title: "Smart Backup", desc: "Auto-backup conversations, memories, and mood logs. Export anytime." },
];

const PRICING_TIERS = [
  { name: "Free", price: "$0", period: "", desc: "Explore the basics", icon: Sparkles, features: ["5 messages/day", "1 AI Module", "Basic mood tracking", "7-day memory"], accent: "#6E7385" },
  { name: "Omni+", price: "$9.99", period: "/mo", desc: "For the individual", icon: Star, features: ["50 messages/day", "All 6 Guides", "Wisdom Council", "Unlimited memory", "Voice & Goals"], accent: "#F5A623", popular: true },
  { name: "Duo", price: "$18", period: "/mo", desc: "For two", icon: Users, features: ["Unlimited messages", "2 members", "All modules + voice", "Family mood dashboard"], accent: "#34D399" },
  { name: "Family", price: "$24.99", period: "/mo", desc: "For the family", icon: Heart, features: ["Unlimited messages", "4 members", "Parental controls", "Age-appropriate AI"], accent: "#A78BFA" },
  { name: "Platinum", price: "$34.99", period: "/mo", desc: "For teams & orgs", icon: Crown, features: ["Unlimited everything", "Unlimited members", "Admin dashboard", "Priority support"], accent: "#E31212" },
];

/* ─── Newsletter Signup ─── */
function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) { toast.error("Please enter a valid email"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setDone(true);
      toast.success("You're on the list!");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (typeof msg === "string" && msg.includes("already")) { setDone(true); toast.info("You're already subscribed!"); }
      else { toast.error("Something went wrong. Please try again."); }
    } finally { setSubmitting(false); }
  };

  return (
    <section className="py-28 md:py-36 px-6 lg:px-12" style={{ background: "#0A0E1A" }} data-testid="newsletter-section">
      <div className="max-w-2xl mx-auto text-center">
        <Reveal>
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-8" style={{ background: "rgba(245,166,35,0.08)" }}>
            <Mail className="w-7 h-7 text-[#F5A623]" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
            Stay in the loop
          </h2>
          <p className="text-[#A1A5B3] mb-10 leading-relaxed max-w-md mx-auto">
            Get product updates, new feature announcements, and exclusive early access — no spam, ever.
          </p>
        </Reveal>

        <Reveal delay={100}>
          {done ? (
            <div className="inline-flex items-center gap-3 px-6 py-4 rounded-full border" style={{ borderColor: "rgba(52,211,153,0.3)", background: "rgba(52,211,153,0.06)" }}>
              <Check className="w-5 h-5 text-emerald-500" />
              <span className="text-sm text-emerald-400 font-medium">You're subscribed — welcome aboard</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto">
              <div className="relative flex-1 w-full">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E7385]" />
                <input
                  data-testid="newsletter-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full h-13 pl-11 pr-4 rounded-full text-sm text-white placeholder-[#6E7385] focus:outline-none transition-all"
                  style={{ background: "#111625", border: "1px solid rgba(255,255,255,0.1)", height: "52px" }}
                />
              </div>
              <button
                data-testid="newsletter-subscribe-btn"
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-7 rounded-full text-white font-medium text-sm transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 flex-shrink-0"
                style={{ background: "#F5A623", height: "52px", boxShadow: "0 0 20px rgba(245,166,35,0.2)" }}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {submitting ? "Joining..." : "Subscribe"}
              </button>
            </form>
          )}
        </Reveal>

        <Reveal delay={200}>
          <p className="text-[11px] text-[#6E7385] mt-6">Unsubscribe anytime. We respect your privacy.</p>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Main Component ─── */
export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(r => setTestimonials(r.data.testimonials || [])).catch(() => {});
  }, []);

  return (
    <>
      {/* ═══════ HERO ═══════ */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-32 px-6 lg:px-12 overflow-hidden" data-testid="hero-section">
        {/* Ambient glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px]" style={{ background: "radial-gradient(ellipse at center, rgba(227,18,18,0.12) 0%, transparent 65%)" }} />
          <div className="absolute top-40 right-0 w-[400px] h-[400px]" style={{ background: "radial-gradient(circle, rgba(245,166,35,0.06) 0%, transparent 70%)" }} />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-8" style={{ borderColor: "rgba(227,18,18,0.3)", background: "rgba(227,18,18,0.06)" }}>
                <div className="w-2 h-2 rounded-full bg-[#E31212] animate-pulse" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E31212]">Now Available Worldwide</span>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <h1
                className="text-5xl sm:text-6xl lg:text-[5.5rem] font-semibold leading-[1.05] tracking-tighter mb-8"
                style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}
              >
                The one friend<br />
                who's <span className="text-[#E31212]">always</span><br />
                on your side
              </h1>
            </Reveal>

            <Reveal delay={200}>
              <p className="text-lg sm:text-xl text-[#A1A5B3] leading-relaxed mb-10 max-w-xl">
                Six AI guides. Twenty-two Wisdom Council mentors. One hundred and thirteen languages. 
                An intelligent companion that remembers who you are, respects where you come from, and grows alongside you.
              </p>
            </Reveal>

            <Reveal delay={300}>
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <Link to="/auth" data-testid="hero-cta-btn">
                  <button className="flex items-center gap-3 px-8 py-4 rounded-full text-white font-medium text-base transition-all duration-300 hover:-translate-y-0.5"
                    style={{ background: "#E31212", boxShadow: "0 0 30px rgba(227,18,18,0.4)" }}>
                    Start your journey <ArrowRight className="w-5 h-5" />
                  </button>
                </Link>
                <Link to="/features" className="flex items-center gap-2 px-6 py-4 text-[#A1A5B3] hover:text-white text-sm transition-colors">
                  Explore features <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <p className="text-xs text-[#6E7385] mt-6">Free forever. No credit card required.</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════ STATS BAR ═══════ */}
      <section className="border-y px-6 lg:px-12 py-16" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-10">
          {[
            { value: "7", label: "AI Guides" },
            { value: "22", label: "Wisdom Mentors" },
            { value: "113", label: "Languages" },
            { value: "24/7", label: "Always Available" },
          ].map((s, i) => (
            <Reveal key={s.label} delay={i * 80} className="text-left md:text-center">
              <p className="text-4xl sm:text-5xl font-light tracking-tight text-white" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif" }}>
                {s.value === "24/7" ? "24/7" : <AnimCounter value={s.value} />}
              </p>
              <p className="text-xs text-[#6E7385] mt-2 uppercase tracking-[0.2em]">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══════ 7 AI MODULES — Bento Grid ═══════ */}
      <section className="py-28 md:py-36 px-6 lg:px-12" data-testid="modules-section">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E31212] mb-4">Modules</p>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-4" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
              Six guides, one companion
            </h2>
            <p className="text-[#A1A5B3] max-w-xl mb-16 text-base leading-relaxed">
              Each module is a distinct AI personality, trained for a specific domain of your life. Switch freely or let Omni detect what you need.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {MODULES.map((m, i) => {
              const Icon = m.icon;
              return (
                <Reveal key={m.name} delay={i * 60} className={`${m.span} rounded-2xl p-7 border transition-all duration-500 group hover:-translate-y-1`}
                  style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: `${m.color}15` }}>
                    <Icon className="w-5 h-5" style={{ color: m.color }} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-medium tracking-tight mb-2 text-white" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif" }}>{m.name}{m.soon && <span className="ml-2 align-middle text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "#4F8CFF22", color: "#4F8CFF" }}>Coming Soon</span>}</h3>
                  <p className="text-sm text-[#6E7385] leading-relaxed">{m.desc}</p>
                  {/* Hover glow */}
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{ background: `radial-gradient(circle at 50% 100%, ${m.color}08, transparent 60%)` }} />
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════ WISDOM COUNCIL ═══════ */}
      <section className="py-28 md:py-36 overflow-hidden" style={{ background: "#0A0E1A" }} data-testid="wisdom-section">
        <div className="px-6 lg:px-12 max-w-6xl mx-auto mb-16">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#F5A623] mb-4">Wisdom Council</p>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-5" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
              Counsel from the ages
            </h2>
            <p className="text-[#A1A5B3] max-w-xl text-base leading-relaxed">
              Converse with 22 historical mentors — each with a unique voice and worldview. Ask Rumi about love. 
              Challenge Socrates on ethics. Let Maya Angelou inspire your courage.
            </p>
          </Reveal>
        </div>

        {/* Scrolling Marquee */}
        <div className="relative mb-20">
          <div className="flex gap-4 overflow-hidden">
            <motion.div
              className="flex gap-4 flex-shrink-0"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            >
              {[...MENTORS, ...MENTORS].map((name, i) => (
                <div key={`${name}-${i}`} className="flex-shrink-0 px-6 py-3.5 rounded-xl border"
                  style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(245,166,35,0.12)" }}>
                  <p className="text-sm font-medium whitespace-nowrap text-[#A1A5B3]" style={{ fontFamily: "'Fraunces', serif" }}>{name}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* 113 Languages Card */}
        <div className="px-6 lg:px-12 max-w-6xl mx-auto">
          <Reveal>
            <div className="rounded-2xl p-8 md:p-12 border flex flex-col md:flex-row items-center gap-8"
              style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(245,166,35,0.15)" }}>
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(245,166,35,0.08)" }}>
                <Globe className="w-10 h-10 text-[#F5A623]" strokeWidth={1} />
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-2xl font-semibold tracking-tight mb-2 text-white" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
                  113 Languages. Your mother tongue included.
                </h3>
                <p className="text-[#A1A5B3] leading-relaxed">
                  Six languages free. Over 100 premium languages with the Global Voice Pack. 
                  Omni doesn't just translate — it understands cultural context, greetings, and communication styles unique to your heritage.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ CAPABILITIES GRID ═══════ */}
      <section className="py-28 md:py-36 px-6 lg:px-12" data-testid="capabilities-section">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E31212] mb-4">Capabilities</p>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-16" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
              Everything you need.<br />Nothing you don't.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {CAPABILITIES.map((f, i) => (
              <Reveal key={f.title} delay={i * 50} className="rounded-2xl p-7 border transition-all duration-500 hover:-translate-y-1 group"
                style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
                <f.icon className="w-5 h-5 text-[#E31212] mb-5" strokeWidth={1.5} />
                <h3 className="text-base font-medium tracking-tight mb-2 text-white">{f.title}</h3>
                <p className="text-sm text-[#6E7385] leading-relaxed">{f.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ TESTIMONIALS ═══════ */}
      {testimonials.length > 0 && (
        <section className="py-28 md:py-36 overflow-hidden" style={{ background: "#0A0E1A" }} data-testid="testimonials-section">
          <div className="px-6 lg:px-12 max-w-6xl mx-auto mb-16">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#F5A623] mb-4">Testimonials</p>
              <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
                Loved worldwide
              </h2>
            </Reveal>
          </div>
          <div className="flex gap-6 overflow-hidden px-6">
            <motion.div
              className="flex gap-6 flex-shrink-0"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
            >
              {[...testimonials, ...testimonials].map((t, i) => (
                <div key={i} className="flex-shrink-0 w-[360px] rounded-2xl p-7 border"
                  style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}>
                  <Quote className="w-5 h-5 text-[#F5A623] mb-4" strokeWidth={1.5} />
                  <p className="text-sm text-[#A1A5B3] leading-relaxed mb-5 line-clamp-4">{t.quote}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: "#E31212" }}>
                      {(t.name || "U")[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{t.name || "User"}</p>
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} className={`w-3 h-3 ${s <= (t.rating || 5) ? "text-[#F5A623] fill-[#F5A623]" : "text-[#6E7385]"}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* ═══════ PRICING PREVIEW ═══════ */}
      <section className="py-28 md:py-36 px-6 lg:px-12" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E31212] mb-4">Pricing</p>
              <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-4" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
                Start free. Grow when ready.
              </h2>
              <p className="text-[#A1A5B3] max-w-lg mx-auto">
                Every plan includes encrypted storage, cultural awareness, and 24/7 availability.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {PRICING_TIERS.map((tier, i) => {
              const Icon = tier.icon;
              return (
                <Reveal key={tier.name} delay={i * 60}>
                  <div
                    className="relative rounded-2xl p-6 border h-full flex flex-col transition-all duration-500 hover:-translate-y-1"
                    style={{
                      background: "#0A0E1A",
                      borderColor: tier.popular ? "rgba(245,166,35,0.4)" : "rgba(255,255,255,0.07)",
                      boxShadow: tier.popular ? "0 0 30px rgba(245,166,35,0.08)" : "none",
                    }}
                  >
                    {tier.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                        style={{ background: "#F5A623", color: "#060912" }}>
                        Popular
                      </div>
                    )}
                    <Icon className="w-5 h-5 mb-4" style={{ color: tier.accent }} strokeWidth={1.5} />
                    <h3 className="text-base font-medium text-white mb-1">{tier.name}</h3>
                    <p className="text-xs text-[#6E7385] mb-4">{tier.desc}</p>
                    <div className="mb-5">
                      <span className="text-3xl font-semibold text-white tracking-tight">{tier.price}</span>
                      <span className="text-sm text-[#6E7385]">{tier.period}</span>
                    </div>
                    <div className="flex flex-col gap-2.5 flex-1">
                      {tier.features.map(f => (
                        <div key={f} className="flex items-start gap-2">
                          <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tier.accent }} />
                          <span className="text-xs text-[#A1A5B3]">{f}</span>
                        </div>
                      ))}
                    </div>
                    <Link to="/auth" className="mt-6">
                      <button className="w-full py-2.5 rounded-full text-sm font-medium transition-all duration-300 hover:brightness-110"
                        style={{
                          background: tier.popular ? "#F5A623" : "rgba(255,255,255,0.05)",
                          color: tier.popular ? "#060912" : "#fff",
                          border: tier.popular ? "none" : "1px solid rgba(255,255,255,0.1)",
                        }}>
                        {tier.price === "$0" ? "Get Started" : "Choose Plan"}
                      </button>
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={400}>
            <div className="text-center mt-10">
              <Link to="/plans" className="inline-flex items-center gap-2 text-sm text-[#A1A5B3] hover:text-white transition-colors">
                Compare all features in detail <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ SECURITY TRUST BAR ═══════ */}
      <section className="py-16 border-y px-6 lg:px-12" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-center gap-12">
          {[
            { icon: Shield, label: "End-to-end encrypted" },
            { icon: Fingerprint, label: "Biometric security" },
            { icon: Globe, label: "GDPR compliant" },
            { icon: Download, label: "Your data, exportable" },
          ].map(item => (
            <div key={item.label} className="flex items-center gap-3">
              <item.icon className="w-5 h-5 text-[#6E7385]" strokeWidth={1.5} />
              <span className="text-sm text-[#A1A5B3]">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════ NEWSLETTER SIGNUP ═══════ */}
      <NewsletterSection />

      {/* ═══════ FINAL CTA ═══════ */}
      <section className="relative py-32 md:py-40 px-6 lg:px-12 overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px]" style={{ background: "radial-gradient(ellipse at center bottom, rgba(227,18,18,0.10) 0%, transparent 65%)" }} />
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <Reveal>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-6" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
              Your journey starts now
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-lg text-[#A1A5B3] mb-10 leading-relaxed">
              Join thousands of users who've made Omni their most trusted companion. Free forever — upgrade when you're ready.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <Link to="/auth" data-testid="final-cta-btn">
              <button className="inline-flex items-center gap-3 px-10 py-5 rounded-full text-white font-medium text-lg transition-all duration-300 hover:-translate-y-1"
                style={{ background: "#E31212", boxShadow: "0 0 40px rgba(227,18,18,0.35)" }}>
                Get Omni Free <ArrowRight className="w-5 h-5" />
              </button>
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
